import { LoadingButton } from '@mui/lab'
import SaveIcon from '@mui/icons-material/Save'
import PublishIcon from '@mui/icons-material/Publish'
import { Button } from 'Common/Supplyvue-ui'
import RestoreIcon from '@mui/icons-material/Restore'
import DownloadIcon from '@mui/icons-material/Download'
import { Box } from '@mui/material'
import {
  MUIToolbarComponentProps,
  WorkcentreSummaryToolbarProps,
} from './Common.types'
import {
  gridFilteredSortedRowIdsSelector,
  GridToolbar,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
} from '@mui/x-data-grid-premium'
import { MutableRefObject, useEffect, useState } from 'react'
import {
  DropzoneInputProps,
  DropzoneRootProps,
  useDropzone,
} from 'react-dropzone'
import toast from 'react-hot-toast'
import { downloadAsCsv } from 'Common/utils/utils'
import { AcceptedData } from 'export-to-csv/output/lib/types'
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium'

const excludedColumns = new Set(['id', 'actions', '__check__'])
const excelMayThrowIssueColumns = new Set(['batchGroupingCode', 'skuCode'])
export const ToolbarComponent = ({
  hasUnsavedRows,
  isSaving,
  saveChanges,
  discardChanges,
}: MUIToolbarComponentProps) => (
  <Box display="flex">
    <Box>
      <LoadingButton
        disabled={!hasUnsavedRows}
        loading={isSaving}
        onClick={saveChanges}
        startIcon={<SaveIcon />}
        loadingPosition="start"
      >
        <span>Save</span>
      </LoadingButton>
      <Button
        disabled={!hasUnsavedRows || isSaving}
        onClick={discardChanges}
        startIcon={<RestoreIcon />}
      >
        Discard all changes
      </Button>
    </Box>
    <Box>
      <GridToolbar />
    </Box>
  </Box>
)

export const TCClosure = ({
  hasUnsavedRows,
  isSaving,
  saveChanges,
  discardChanges,
}: MUIToolbarComponentProps) => {
  return () => (
    <ToolbarComponent
      discardChanges={discardChanges}
      saveChanges={saveChanges}
      hasUnsavedRows={hasUnsavedRows}
      isSaving={isSaving}
    />
  )
}

const WorkcentreSummaryToolbar = ({
  hasWorkcentresSelected,
  isPublishing,
  publichWorkcentres,
}: WorkcentreSummaryToolbarProps) => {
  return (
    <Box display="flex" justifyContent="space-between" width="100%">
      <Box>
        <LoadingButton
          disabled={!hasWorkcentresSelected}
          loading={isPublishing}
          onClick={publichWorkcentres}
          startIcon={<PublishIcon />}
          loadingPosition="start"
        >
          <span>Publish</span>
        </LoadingButton>
      </Box>
      <Box>
        <GridToolbar />
      </Box>
    </Box>
  )
}

export const WorkcentreSummaryPublishClosure = ({
  hasWorkcentresSelected,
  isPublishing,
  publichWorkcentres,
}: WorkcentreSummaryToolbarProps) => {
  return () => (
    <WorkcentreSummaryToolbar
      hasWorkcentresSelected={hasWorkcentresSelected}
      isPublishing={isPublishing}
      publichWorkcentres={publichWorkcentres}
    />
  )
}

type UploadButtonProps = {
  rootProps: DropzoneRootProps
  inputProps: DropzoneInputProps
  open: () => void
  isUploading: boolean
}

const UploadButton = ({
  rootProps,
  inputProps,
  open,
  isUploading,
}: UploadButtonProps) => {
  return (
    <Box {...rootProps}>
      <input {...inputProps} />
      <LoadingButton
        disabled={isUploading}
        loading={isUploading}
        onClick={open}
        startIcon={<PublishIcon />}
        loadingPosition="start"
      >
        <span>Upload</span>
      </LoadingButton>
    </Box>
  )
}
/*** Download as csv button for tool bar */
type ToolBarDownloadCSVButtonProps = {
  fileName: string
  data: Record<string, AcceptedData>[]
  apiRef?: MutableRefObject<GridApiPremium> | null
  downloadVisibleColumns?: boolean
  dataConfig?: {
    required: string[]
    optional: string[]
  }
}
const ToolBarDownloadCSVButton = ({
  fileName,
  data,
  apiRef = null,
  downloadVisibleColumns = true,
  dataConfig = undefined,
}: ToolBarDownloadCSVButtonProps) => {
  const [isDownloading, setIsDownloading] = useState(false)
  const prepareTableData = () => {
    if (!apiRef) {
      return []
    }
    // Step 1: Get visible columns
    const visibleColumns = downloadVisibleColumns
      ? apiRef.current.getVisibleColumns()
      : apiRef.current.getAllColumns()

    // Step 1.5: Filter columns based on dataConfig if provided
    // Combine required and optional columns
    const columnsMerge = [
      ...(dataConfig?.required ?? []),
      ...(dataConfig?.optional ?? []),
    ]
    const columnsToInclude =
      dataConfig && dataConfig.required.length > 0
        ? visibleColumns.filter((col) => columnsMerge.includes(col.field))
        : visibleColumns
    // Step 2: Get filtered and sorted rows
    const rowIds = gridFilteredSortedRowIdsSelector(apiRef) // Row IDs in sorted order
    const rowModels = apiRef.current.getRowModels() // Map of all rows

    // Step 3: Prepare the data
    const data = rowIds.map((id) => {
      const row = rowModels.get(id)
      const rowData: Record<string, AcceptedData> = {}
      columnsToInclude.forEach((col) => {
        if (excludedColumns.has(col.field)) {
          return
        }
        if (excelMayThrowIssueColumns.has(col.field)) {
          rowData[col.field] = `'${row[col.field]}`
          return
        }
        rowData[col.field] = row[col.field]
      })
      return rowData
    })

    // Step 4: Prepare headers

    return data
  }

  const downloadFile = () => {
    try {
      setIsDownloading(true)
      if (apiRef) {
        const rows = prepareTableData()
        downloadAsCsv(fileName, rows)
      } else {
        downloadAsCsv(fileName, data)
      }
      setIsDownloading(false)
    } catch (error) {
      console.error('Error downloading CSV:', error)
      setIsDownloading(false)
    }
  }
  return (
    <Box>
      <LoadingButton
        loading={isDownloading}
        onClick={downloadFile}
        startIcon={<DownloadIcon />}
        loadingPosition="start"
      >
        <span>Download</span>
      </LoadingButton>
    </Box>
  )
}
//

export const PolicySettingsTableToolBar = ({
  hasUnsavedRows,
  isSaving,
  saveChanges,
  discardChanges,
  dataToDownload,
  fileName,
  apiRef = null,
  downloadVisibleColumns = true,
  dataConfig = undefined,
}: MUIToolbarComponentProps & {
  fileName: string
}) => {
  return () => (
    <Box display="flex">
      <Box>
        <LoadingButton
          disabled={!hasUnsavedRows}
          loading={isSaving}
          onClick={saveChanges}
          startIcon={<SaveIcon />}
          loadingPosition="start"
        >
          <span>Save</span>
        </LoadingButton>
        <Button
          disabled={!hasUnsavedRows || isSaving}
          onClick={discardChanges}
          startIcon={<RestoreIcon />}
        >
          DISCARD CHANGES
        </Button>
      </Box>
      <Box>
        <GridToolbarContainer>
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <GridToolbarDensitySelector
            slotProps={{ tooltip: { title: 'Change density' } }}
          />
          <Box sx={{ flexGrow: 1 }} />
          <ToolBarDownloadCSVButton
            fileName={fileName}
            data={dataToDownload ?? []}
            apiRef={apiRef}
            downloadVisibleColumns={downloadVisibleColumns}
            dataConfig={dataConfig}
          />
        </GridToolbarContainer>
      </Box>
    </Box>
  )
}

type WCSettingsTableToolBarProps = {
  isUploading: boolean
  uploadFile: (file: File) => void
  dataToDownload: Record<string, AcceptedData>[]
  fileName: string
}

export const WCSettingsTableToolBar = ({
  isUploading,
  uploadFile,
  dataToDownload,
  fileName,
}: WCSettingsTableToolBarProps) => {
  const { acceptedFiles, fileRejections, getRootProps, getInputProps, open } =
    useDropzone({
      accept: { 'text/csv': ['.csv'] },
      maxFiles: 1,
      noClick: true,
      noKeyboard: true,
    })

  useEffect(() => {
    if (fileRejections.length) {
      toast.error('Invalid file')
    } else if (acceptedFiles.length) {
      uploadFile(acceptedFiles[0])
    }
  }, [acceptedFiles, fileRejections])
  return () => (
    <Box display="flex">
      <GridToolbarContainer>
        <ToolBarDownloadCSVButton fileName={fileName} data={dataToDownload} />
      </GridToolbarContainer>
      <Box>
        <UploadButton
          rootProps={getRootProps()}
          inputProps={getInputProps()}
          open={open}
          isUploading={isUploading}
        />
      </Box>
    </Box>
  )
}
